<template>
    <div class="dnd-flow" @drop="onDrop">
        <!-- Sidebar -->
        <aside class="sidebar" v-show="sidebarOpen">
            <div class="sidebar-header">
                <div class="d-flex justify-space-between align-center mb-2">
                    <v-btn icon="mdi-arrow-left" variant="text" size="small" @click="$emit('back')"></v-btn>
                    <v-btn icon="mdi-menu-open" variant="text" size="small" color="grey" @click="sidebarOpen = false"
                        title="Collapse Sidebar"></v-btn>
                </div>
                <h3>{{ config.title || 'New Flow' }}</h3>
                <p class="subtitle">{{ config.workflow_type }}</p>
                <p class="instruction">Drag jobs to configure.</p>
            </div>

            <div class="nodes">
                <div class="sidebar-node start-node" :draggable="true" @dragstart="onDragStart($event, 'start')">
                    <v-icon class="me-2" color="success">mdi-play-circle-outline</v-icon>
                    Start
                </div>

                <div class="sidebar-node unconfirmed" :draggable="true"
                    @dragstart="onDragStart($event, WORKFLOW_NODE_TYPES.UNCONFIRMED)">
                    <v-icon class="me-2" :color="WORKFLOW_NODE_COLORS[WORKFLOW_NODE_TYPES.UNCONFIRMED]">
                        {{ WORKFLOW_NODE_ICONS[WORKFLOW_NODE_TYPES.UNCONFIRMED] }}
                    </v-icon>
                    Unconfirmed Appt Reminder
                </div>

                <div class="sidebar-node reminder-all" :draggable="true"
                    @dragstart="onDragStart($event, WORKFLOW_NODE_TYPES.REMINDER_ALL)">
                    <v-icon class="me-2" :color="WORKFLOW_NODE_COLORS[WORKFLOW_NODE_TYPES.REMINDER_ALL]">
                        {{ WORKFLOW_NODE_ICONS[WORKFLOW_NODE_TYPES.REMINDER_ALL] }}
                    </v-icon>
                    Appt Reminder (All)
                </div>

                <div class="sidebar-node notification" :draggable="true"
                    @dragstart="onDragStart($event, WORKFLOW_NODE_TYPES.NOTIFICATION)">
                    <v-icon class="me-2" :color="WORKFLOW_NODE_COLORS[WORKFLOW_NODE_TYPES.NOTIFICATION]">
                        {{ WORKFLOW_NODE_ICONS[WORKFLOW_NODE_TYPES.NOTIFICATION] }}
                    </v-icon>
                    Appt Notification
                </div>

                <div class="sidebar-node end-node" :draggable="true" @dragstart="onDragStart($event, 'end')">
                    <v-icon class="me-2" color="error">mdi-stop-circle-outline</v-icon>
                    End
                </div>
            </div>

            <div class="sidebar-actions">
                <v-btn color="success" block @click="saveWorkflow" :loading="saving" :disabled="saving">
                    {{ isEditing ? 'Update Workflow' : 'Save Workflow' }}
                </v-btn>
            </div>
        </aside>

        <!-- Vue Flow Canvas -->
        <div class="vue-flow-wrapper" ref="flowWrapper">
            <VueFlow v-model="elements" :default-zoom="1" :min-zoom="0.2" :max-zoom="4" @dragover="onDragOver"
                @pane-ready="onPaneReady" @connect="onConnect" @edge-click="onEdgeClick" @node-click="onNodeClick"
                @pane-click="onPaneClick" class="basicflow">

                <!-- Expand Sidebar Button -->
                <Panel position="top-left" v-if="!sidebarOpen">
                    <v-btn icon="mdi-menu" size="small" color="white" elevation="2" @click="sidebarOpen = true"
                        title="Expand Sidebar"></v-btn>
                </Panel>

                <Background pattern-color="#81818a" gap="16" />

                <Controls />

                <MiniMap />

                <!-- Register Custom Node -->
                <template #node-custom="props">
                    <CustomNode v-bind="props" />
                </template>

                <!-- Register Circle Node -->
                <template #node-circle="props">
                    <CircleNode v-bind="props" />
                </template>
            </VueFlow>
        </div>

        <aside class="properties-sidebar" v-if="selectedNode" :style="{ width: panelWidth + 'px' }">
            <div class="resize-handle" @mousedown.prevent="startResize"></div>
            <PropertiesPanel :node="selectedNode" @close="selectedNode = null" @delete="onDeleteNode"
                @view-job="showJobDialog" />
        </aside>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top right">
            {{ snackbar.text }}
            <template v-slot:actions>
                <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
            </template>
        </v-snackbar>
        <v-bottom-sheet v-model="jobDialog">
            <v-card style="max-height: 90vh; display: flex; flex-direction: column;">
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Associated Jobs</span>
                    <v-btn icon="mdi-close" variant="text" @click="jobDialog = false"></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-0 overflow-y-auto" style="flex: 1;">
                    <JobsView :jobs_list="jobs" />
                </v-card-text>
            </v-card>
        </v-bottom-sheet>
    </div>
</template>

<script>
import { VueFlow, useVueFlow, Panel } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { MiniMap } from '@vue-flow/minimap'
import { ref } from 'vue'
import CustomNode from './CustomNode.vue'
import CircleNode from './CircleNode.vue'
import PropertiesPanel from './PropertiesPanel.vue'
import { WORKFLOW_NODE_TYPES, WORKFLOW_NODE_ICONS, WORKFLOW_NODE_COLORS } from '../../taskEngine/constants'
import WorkflowService from '../../services/workflowService'
import TaskEngineService from '../../services/taskEngineService'
import JobsView from '../AdminSettings/JobsView.vue'
// Import standard Vue Flow styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import '@vue-flow/minimap/dist/style.css'



export default {
    name: 'WorkFlowBuilder',
    components: {
        VueFlow,
        Panel,
        Background,
        Controls,
        MiniMap,
        CustomNode,
        CircleNode,
        PropertiesPanel,
        JobsView
    },
    props: {
        config: {
            type: Object,
            default: () => ({ title: 'New Workflow', workflow_type: 'PatientAppointment' })
        }
    },
    emits: ['back', 'save'],
    setup() {
        const flowWrapper = ref(null)
        const { removeNodes } = useVueFlow()
        return {
            flowWrapper,
            removeNodes
        }
    },
    data() {
        return {
            elements: [],
            flowInstance: null,
            WORKFLOW_NODE_TYPES,
            WORKFLOW_NODE_ICONS,
            WORKFLOW_NODE_COLORS,
            saving: false,
            selectedNode: null,
            sidebarOpen: true,
            snackbar: {
                show: false,
                text: '',
                color: 'success'
            },
            nodeIdCounter: 0,
            panelWidth: 350,
            isResizing: false,
            startX: 0,
            startWidth: 0,
            jobDialog: false,
            jobs: []
        }
    },
    computed: {
        isEditing() {
            return !!this.config.workflow_id
        }
    },
    created() {
        if (this.config && this.config.workflow_data) {
            const { nodes, edges } = this.config.workflow_data
            if (nodes || edges) {
                this.elements = [...(nodes || []), ...(edges || [])]
            }
        }
        this.syncNodeIdCounter()
    },
    methods: {
        onPaneReady(instance) {
            this.flowInstance = instance
            if (this.config && this.config.workflow_data && this.config.workflow_data.viewport) {
                const { x, y, zoom } = this.config.workflow_data.viewport
                instance.setViewport({ x, y, zoom })
            }
        },
        onConnect(params) {
            if (this.flowInstance) {
                this.flowInstance.addEdges(params)
            }
        },
        onEdgeClick({ edge }) {
            this.flowInstance.removeEdges(edge)

        },
        onNodeClick({ node }) {
            if (node.type === 'custom') {
                this.selectedNode = node
            } else {
                this.selectedNode = null
            }
        },
        onPaneClick() {
            this.selectedNode = null
        },
        onDeleteNode(nodeId) {
            if (nodeId) {
                this.removeNodes(nodeId)
                this.selectedNode = null
            }
        },
        onDragStart(event, nodeType) {
            if (event.dataTransfer) {
                event.dataTransfer.setData('application/vueflow', nodeType)
                event.dataTransfer.effectAllowed = 'move'
            }
        },
        onDragOver(event) {
            event.preventDefault()
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move'
            }
        },
        onDrop(event) {
            const type = event.dataTransfer?.getData('application/vueflow')
            if (!type) return

            // Adjust for scaling / positioning using the instance project method
            const { left, top } = this.flowWrapper.getBoundingClientRect()

            // Default position if projection fails or instance not ready
            let position = { x: event.clientX - left, y: event.clientY - top }

            if (this.flowInstance) {
                position = this.flowInstance.project(position)
            }

            let newNode = {
                id: this.getId(),
                position,
            }

            if (type === 'start' || type === 'end') {
                const existingNodes = this.flowInstance.getNodes
                const hasType = existingNodes.some(n => n.data?.type === type)

                if (hasType) {
                    this.showSnackbar(`You can only have one ${type} node per workflow.`, 'warning')
                    return
                }

                newNode.type = 'circle'
                newNode.label = type === 'start' ? 'Start' : 'End'
                newNode.data = { type: type }
            } else {
                newNode.type = 'custom'
                newNode.taskType = type
                newNode.data = {
                    sendMail: false,
                    sendSms: false,
                    emailBody: '',
                    smsBody: '',
                    emailSubject: '',
                    jobType: type,
                    triggerEvent: '', // only for action notifications
                    job_id: null,
                    job_status: null,
                    processInsuranceDue: false,
                    insuranceDueAppointments: []
                }

                newNode.data.icon = this.WORKFLOW_NODE_ICONS[type] || 'mdi-cog'
                newNode.data.color = this.WORKFLOW_NODE_COLORS[type] || 'grey'

                if (type === this.WORKFLOW_NODE_TYPES.UNCONFIRMED) {
                    newNode.label = 'Unconfirmed Appt Reminder'
                } else if (type === this.WORKFLOW_NODE_TYPES.REMINDER_ALL) {
                    newNode.label = 'Appt Reminder (All)'
                } else if (type === this.WORKFLOW_NODE_TYPES.NOTIFICATION) {
                    newNode.label = 'Appt Notification'
                }
            }

            this.elements.push(newNode)
        },
        async saveWorkflow() {
            if (!this.flowInstance) {
                console.error("Workflow instance not ready")
                return
            }

            this.saving = true
            try {
                const flowObject = this.flowInstance.toObject()
                const workflowPayload = {
                    ...this.config,
                    workflow_data: {
                        nodes: flowObject.nodes,
                        edges: flowObject.edges,
                        viewport: flowObject.viewport
                    }
                }

                console.log("Saving Workflow:", workflowPayload)

                if (this.isEditing) {
                    await WorkflowService.updateWorkflow(this.config.workflow_id, workflowPayload)
                    this.showSnackbar('Workflow updated successfully!', 'success')
                } else {
                    await WorkflowService.saveWorkflow(workflowPayload)
                    this.showSnackbar('Workflow saved successfully!', 'success')
                }

                this.$emit('save')
            } catch (error) {
                console.error("Error saving workflow:", error)
                this.showSnackbar('Failed to save workflow. Please try again.', 'error')
            } finally {
                this.saving = false
            }
        },
        async showJobDialog(jobId) {
            this.jobDialog = true
            let res = await TaskEngineService.getJobById(jobId)
            this.jobs = res.data
        },
        showSnackbar(text, color = 'success') {
            this.snackbar.text = text
            this.snackbar.color = color
            this.snackbar.show = true
        },
        getId() {
            return `node_${this.nodeIdCounter++}`
        },
        syncNodeIdCounter() {
            if (this.elements.length > 0) {
                const ids = this.elements
                    .filter(el => el.id && el.id.startsWith('node_'))
                    .map(el => parseInt(el.id.replace('node_', '')))
                    .filter(n => !isNaN(n))

                if (ids.length > 0) {
                    this.nodeIdCounter = Math.max(...ids) + 1
                }
            }
        },
        startResize(e) {
            this.isResizing = true
            this.startX = e.clientX
            this.startWidth = this.panelWidth
            document.addEventListener('mousemove', this.resize)
            document.addEventListener('mouseup', this.stopResize)
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
        },
        resize(e) {
            if (!this.isResizing) return
            const dx = this.startX - e.clientX
            const newWidth = this.startWidth + dx
            if (newWidth > 300 && newWidth < 800) {
                this.panelWidth = newWidth
            }
        },
        stopResize() {
            this.isResizing = false
            document.removeEventListener('mousemove', this.resize)
            document.removeEventListener('mouseup', this.stopResize)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
    }
}
</script>

<style scoped>
.dnd-flow {
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
    font-family: 'Inter', sans-serif;
}

.vue-flow-wrapper {
    flex-grow: 1;
    height: 100%;
    background: #f8f9fa;
}

.sidebar {
    width: 320px;
    height: 100%;
    background: white;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
    z-index: 10;
}

.properties-sidebar {
    height: 100%;
    background: white;
    border-left: 1px solid #e0e0e0;
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
    z-index: 10;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: width 0.1s;
}

.resize-handle {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    z-index: 11;
    background: transparent;
    transition: background 0.2s;
}

.resize-handle:hover,
.resize-handle:active {
    background: rgba(33, 150, 243, 0.1);
    border-left: 2px solid #2196f3;
}

.sidebar-header {
    padding: 24px;
    border-bottom: 1px solid #eee;
}

.sidebar-header h3 {
    margin: 0 0 4px 0;
    font-size: 18px;
    color: #333;
}

.sidebar-header .subtitle {
    margin: 0 0 8px 0;
    color: #2196f3;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.sidebar-header .instruction {
    margin: 0;
    color: #666;
    font-size: 13px;
}

.sidebar-actions {
    padding: 24px;
    border-top: 1px solid #eee;
}

.nodes {
    padding: 24px;
    flex: 1;
    overflow-y: auto;
}

.sidebar-node {
    background: white;
    border: 1px solid #ddd;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 12px;
    cursor: grab;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #444;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.sidebar-node:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-color: #2196f3;
    color: #2196f3;
}

.sidebar-node .v-icon {
    color: inherit;
}

/* Vue Flow Styles Overrides */
.vue-flow__minimap {
    transform: scale(75%);
    transform-origin: bottom right;
    bottom: 20px !important;
    right: 20px !important;
}

.vue-flow__controls {
    bottom: 20px !important;
    left: 20px !important;
}
</style>
